import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../supabaseClient";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Admin.css";
import CloudinaryUploadWidget from "../components/CloudinaryUploadWidget";

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryVideos, setGalleryVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("images");
  const navigate = useNavigate();

  // Image upload states
  const [imageTitle, setImageTitle] = useState("");
  const [imageDescription, setImageDescription] = useState("");
  const [selectedCompanyForImage, setSelectedCompanyForImage] = useState("");
  const [publicId, setPublicId] = useState("");

  // Video upload states
  const [videoUrl, setVideoUrl] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [videoDescription, setVideoDescription] = useState("");
  const [selectedCompanyForVideo, setSelectedCompanyForVideo] = useState("");
  const [videoUploading, setVideoUploading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Cloudinary config
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
  const uwConfig = {
    cloudName,
    uploadPreset,
    multiple: false,
    sources: ["local", "url"],
    folder: "projects",
    clientAllowedFormats: ["jpg", "jpeg", "png", "gif", "webp"],
    maxImageFileSize: 5000000,
    cropping: false,
    showAdvancedOptions: false,
    theme: "purple",
  };

  // Memoize fetchData to prevent infinite re-renders
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch companies
      const { data: companiesData, error: companiesError } = await supabase
        .from("companies")
        .select("id, name");

      if (companiesError) {
        console.error("Companies fetch error:", companiesError);
      } else {
        setCompanies(companiesData || []);
        if (companiesData && companiesData.length > 0 && !selectedCompanyForImage) {
          setSelectedCompanyForImage(companiesData[0].id);
          setSelectedCompanyForVideo(companiesData[0].id);
        }
      }

      // Fetch gallery images
      const { data: imagesData, error: imagesError } = await supabase
        .from("gallery_images")
        .select(
          `
          id, 
          image_url, 
          title, 
          description, 
          created_at,
          companies(name)
        `
        )
        .order("created_at", { ascending: false });

      if (imagesError) {
        console.error("Gallery images fetch error:", imagesError);
      } else {
        setGalleryImages(imagesData || []);
      }

      // Fetch gallery videos
      const { data: videosData, error: videosError } = await supabase
        .from("gallery_videos")
        .select(
          `
          id, 
          video_url, 
          title, 
          description, 
          created_at,
          companies(name)
        `
        )
        .order("created_at", { ascending: false });

      if (videosError) {
        console.error("Gallery videos fetch error:", videosError);
      } else {
        setGalleryVideos(videosData || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyForImage]); // Add dependency

  // Check user authentication
  useEffect(() => {
    const checkUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate("/admin/login");
        return;
      }
      setUser(user);
    };
    
    checkUser();
  }, [navigate]); // Add navigate as dependency

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle Cloudinary upload result
  useEffect(() => {
    const saveImageToDatabase = async () => {
      if (publicId && imageTitle && selectedCompanyForImage) {
        const publicUrl = `https://res.cloudinary.com/${cloudName}/image/upload/${publicId}`;
        
        try {
          const { error } = await supabase
            .from("gallery_images")
            .insert({
              image_url: publicUrl,
              title: imageTitle,
              description: imageDescription,
              company_id: selectedCompanyForImage,
            });

          if (error) {
            console.error("Error saving image:", error);
            window.alert("Error saving image to DB: " + error.message);
          } else {
            setImageTitle("");
            setImageDescription("");
            setPublicId("");
            fetchData();
            window.alert("Image uploaded successfully!");
          }
        } catch (error) {
          console.error("Database error:", error);
          window.alert("Error saving image: " + error.message);
        }
      }
    };

    saveImageToDatabase();
  }, [publicId, imageTitle, selectedCompanyForImage, imageDescription, cloudName, fetchData]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleVideoUpload = async (e) => {
    e.preventDefault();
    if (!videoUrl || !selectedCompanyForVideo) return;

    setVideoUploading(true);
    try {
      const { error } = await supabase.from("gallery_videos").insert({
        video_url: videoUrl,
        title: videoTitle,
        description: videoDescription,
        company_id: selectedCompanyForVideo,
      });

      if (error) {
        throw error;
      }

      // Reset form and refresh data
      setVideoUrl("");
      setVideoTitle("");
      setVideoDescription("");
      fetchData();

      window.alert("Video added successfully!");
    } catch (error) {
      console.error("Error adding video:", error);
      window.alert("Error adding video: " + error.message);
    } finally {
      setVideoUploading(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const { error } = await supabase
        .from("gallery_images")
        .delete()
        .eq("id", imageId);

      if (error) {
        throw error;
      }

      fetchData();
      window.alert("Image deleted successfully!");
    } catch (error) {
      console.error("Error deleting image:", error);
      window.alert("Error deleting image: " + error.message);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm("Are you sure you want to delete this video?")) return;

    try {
      const { error } = await supabase
        .from("gallery_videos")
        .delete()
        .eq("id", videoId);

      if (error) {
        throw error;
      }

      fetchData();
      window.alert("Video deleted successfully!");
    } catch (error) {
      console.error("Error deleting video:", error);
      window.alert("Error deleting video: " + error.message);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1>Admin Dashboard</h1>
              <p>Welcome, {user?.email}</p>
            </div>
            <div className="d-flex gap-2">
              <a href="/" className="btn btn-outline-primary">
                View Website
              </a>
              <button onClick={handleLogout} className="btn btn-outline-danger">
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-4">
        {/* Navigation Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "images" ? "active" : ""}`}
              onClick={() => setActiveTab("images")}
            >
              Manage Images
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "videos" ? "active" : ""}`}
              onClick={() => setActiveTab("videos")}
            >
              Manage Videos
            </button>
          </li>
        </ul>

        {/* Images Tab */}
        {activeTab === "images" && (
          <div className="row">
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5>Upload New Image</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label">Select Company</label>
                    <select
                      className="form-select"
                      value={selectedCompanyForImage}
                      onChange={(e) =>
                        setSelectedCompanyForImage(e.target.value)
                      }
                      required
                      autoComplete="organization"
                    >
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={imageTitle}
                      onChange={(e) => setImageTitle(e.target.value)}
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={imageDescription}
                      onChange={(e) => setImageDescription(e.target.value)}
                      autoComplete="off"
                    ></textarea>
                  </div>
                  {/* Cloudinary Upload Widget */}
                  <CloudinaryUploadWidget
                    uwConfig={uwConfig}
                    setPublicId={setPublicId}
                    setUploading={setUploading}
                  />
                  {uploading && (
                    <div className="text-center my-2">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                      <div>Uploading image...</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-8">
              <div className="card">
                <div className="card-header">
                  <h5>Gallery Images</h5>
                </div>
                <div className="card-body">
                  {galleryImages.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <p>No images found. Upload your first image!</p>
                    </div>
                  ) : (
                    <div className="row">
                      {galleryImages.map((image) => (
                        <div key={image.id} className="col-md-6 col-lg-4 mb-3">
                          <div className="admin-image-card">
                            <img
                              src={image.image_url}
                              alt={image.title}
                              className="admin-image-preview"
                              loading="lazy"
                            />
                            <div className="admin-image-info">
                              <h6>{image.title}</h6>
                              <p className="text-muted small">
                                {image.companies?.name}
                              </p>
                              <button
                                onClick={() => deleteImage(image.id)}
                                className="btn btn-sm btn-danger"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === "videos" && (
          <div className="row">
            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5>Add New Video</h5>
                </div>
                <div className="card-body">
                  <form onSubmit={handleVideoUpload}>
                    <div className="mb-3">
                      <label className="form-label">Select Company</label>
                      <select
                        className="form-select"
                        value={selectedCompanyForVideo}
                        onChange={(e) =>
                          setSelectedCompanyForVideo(e.target.value)
                        }
                        required
                        autoComplete="organization"
                      >
                        {companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">YouTube Video URL</label>
                      <input
                        type="url"
                        className="form-control"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=..."
                        required
                        autoComplete="url"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={videoTitle}
                        onChange={(e) => setVideoTitle(e.target.value)}
                        required
                        autoComplete="off"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={videoDescription}
                        onChange={(e) => setVideoDescription(e.target.value)}
                        autoComplete="off"
                      ></textarea>
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary w-100"
                      disabled={videoUploading}
                    >
                      {videoUploading ? "Adding..." : "Add Video"}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            <div className="col-md-8">
              <div className="card">
                <div className="card-header">
                  <h5>Gallery Videos</h5>
                </div>
                <div className="card-body">
                  {galleryVideos.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <p>No videos found. Add your first video!</p>
                    </div>
                  ) : (
                    <div className="row">
                      {galleryVideos.map((video) => (
                        <div key={video.id} className="col-md-6 mb-3">
                          <div className="admin-video-card">
                            <div className="admin-video-info">
                              <h6>{video.title}</h6>
                              <p className="text-muted small">
                                {video.companies?.name}
                              </p>
                              <p className="small">{video.description}</p>
                              <a
                                href={video.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary me-2"
                              >
                                View Video
                              </a>
                              <button
                                onClick={() => deleteVideo(video.id)}
                                className="btn btn-sm btn-danger"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}